/**
 * Part Change History Drawer.
 *
 * Lists all bulk-propose submissions the supplier has made for one
 * (part, customer) pair, each with its spawned PO line outcomes pulled
 * live so accept/reject decisions made post-submit are reflected.
 *
 * Mounted from the supplier Stock Planning grid and Stock Levels page,
 * opened via the History button alongside Propose.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import { Loader2, History as HistoryIcon, Check, X, Clock, Ban } from 'lucide-react';
import { toast } from 'sonner';

import { partChangeAPI } from '../../../lib/api';
import type {
  PartChangeHistoryEntry,
  PartChangeHistoryLine,
} from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';

interface PartChangeHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partNumber: string;
  partDescription?: string;
  customerId: number;
}

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const formatDateOnly = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString();
};

const outcomeChip = (outcome: PartChangeHistoryLine['outcome']) => {
  switch (outcome) {
    case 'accepted':
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-[10px]">
          <Check className="h-3 w-3 mr-1" />Accepted
        </Badge>
      );
    case 'rejected':
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 text-[10px]">
          <X className="h-3 w-3 mr-1" />Rejected
        </Badge>
      );
    case 'cancelled':
      return (
        <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-300 text-[10px]">
          <Ban className="h-3 w-3 mr-1" />Cancelled
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-[10px]">
          <Clock className="h-3 w-3 mr-1" />Pending
        </Badge>
      );
  }
};

const rollupStatus = (lines: PartChangeHistoryLine[]) => {
  if (lines.length === 0) return { label: 'No spawned lines', tone: 'gray' as const };
  const counts = lines.reduce<Record<string, number>>((acc, l) => {
    acc[l.outcome] = (acc[l.outcome] ?? 0) + 1;
    return acc;
  }, {});
  if (counts['pending']) return { label: `Pending (${counts['pending']}/${lines.length})`, tone: 'amber' as const };
  const accepted = counts['accepted'] ?? 0;
  const rejected = counts['rejected'] ?? 0;
  const cancelled = counts['cancelled'] ?? 0;
  if (accepted === lines.length) return { label: 'Accepted', tone: 'green' as const };
  if (rejected === lines.length) return { label: 'Rejected', tone: 'red' as const };
  if (cancelled === lines.length) return { label: 'Cancelled', tone: 'gray' as const };
  return { label: `Mixed (${accepted}/${lines.length} accepted)`, tone: 'blue' as const };
};

const toneClass: Record<string, string> = {
  green: 'bg-green-50 text-green-700 border-green-300',
  red: 'bg-red-50 text-red-700 border-red-300',
  amber: 'bg-amber-50 text-amber-700 border-amber-300',
  blue: 'bg-blue-50 text-blue-700 border-blue-300',
  gray: 'bg-gray-50 text-gray-600 border-gray-300',
};

export default function PartChangeHistoryDrawer({
  open,
  onOpenChange,
  partNumber,
  partDescription,
  customerId,
}: PartChangeHistoryDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState<PartChangeHistoryEntry[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    partChangeAPI
      .getChangeHistory({ part_number: partNumber, customer_id: customerId })
      .then((res) => {
        if (cancelled) return;
        setEntries(res.data.results);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        toast.error(getApiErrorMessage(err, 'Failed to load change history'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, partNumber, customerId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HistoryIcon className="h-5 w-5 text-primary" />
            Change history — <span className="text-primary">{partNumber}</span>
          </DialogTitle>
          <DialogDescription>
            {partDescription && <span>{partDescription} · </span>}
            All bulk-propose submissions you've made for this part. Per-line outcomes update as the customer accepts or rejects.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : entries.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No change requests yet for this part.
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            {entries.map((entry) => {
              const requested = parseFloat(entry.requested_total_qty);
              const original = parseFloat(entry.original_total_qty);
              const delta = requested - original;
              const deltaClass =
                delta < 0 ? 'text-red-600' : delta > 0 ? 'text-green-600' : 'text-muted-foreground';
              const status = rollupStatus(entry.spawned_lines);
              return (
                <div key={entry.id} className="border rounded-lg p-4 bg-card">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-semibold">PR #{entry.id}</span>
                        <Badge variant="outline" className={`text-[10px] ${toneClass[status.tone]}`}>
                          {status.label}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Submitted {formatDate(entry.created_at)} · by {entry.submitted_by_name}
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="font-mono font-semibold">
                        {original} → <span className="text-primary">{requested}</span>
                      </div>
                      <div className={`text-xs font-mono ${deltaClass}`}>
                        {delta === 0 ? '0' : delta > 0 ? `+${delta}` : delta} ·{' '}
                        {entry.spawned_line_count} line{entry.spawned_line_count === 1 ? '' : 's'}
                      </div>
                    </div>
                  </div>

                  <div className="text-sm bg-muted/40 border rounded-md p-2.5 mb-2">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">
                      Reason
                    </div>
                    <div>{entry.reason || '—'}</div>
                    {entry.comments && (
                      <>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground mt-2 mb-0.5">
                          Comments
                        </div>
                        <div className="italic">{entry.comments}</div>
                      </>
                    )}
                  </div>

                  {entry.spawned_lines.length > 0 ? (
                    <div className="border rounded-md overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50">
                          <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            <th className="text-left px-2 py-1.5 font-medium">PO / Line</th>
                            <th className="text-left px-2 py-1.5 font-medium">Promise</th>
                            <th className="text-right px-2 py-1.5 font-medium">Proposed</th>
                            <th className="text-right px-2 py-1.5 font-medium">Current</th>
                            <th className="text-left px-2 py-1.5 font-medium">Outcome</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entry.spawned_lines.map((l) => (
                            <tr key={l.po_line_id} className="border-t">
                              <td className="px-2 py-1.5 font-medium">
                                {l.po_number} / L{l.line_number}
                              </td>
                              <td className="px-2 py-1.5 text-muted-foreground">
                                {formatDateOnly(l.promise_date)}
                              </td>
                              <td className="px-2 py-1.5 text-right font-mono">{l.proposed_qty}</td>
                              <td className="px-2 py-1.5 text-right font-mono">{l.current_qty}</td>
                              <td className="px-2 py-1.5">{outcomeChip(l.outcome)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground italic">
                      No spawned lines linked — this submission may pre-date audit linking.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
